import { useEffect, useState, forwardRef } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { RouteConstants } from '../../constants/RouteConstants';
import { NewOrganizationFormLayout, sessionTokenSchema } from '../../util/constants';
import InputField from '../shared/Fields/InputField';
import { setToastData } from '../../app/toastReducer';
import { getFailureMessage } from '../../util/ErrorUtil';

import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';

import Card from '../../components/shared/cards/Card';
import { HTTP_STATUS } from '../../http/constants/http.status';

import { SettingIcon } from '../../assets/components/svgs';
import { init, t } from 'i18next';
import {
  getSessionTokenSettings,
  updateSessionTokenSettings,
  updateOauthSettingsService,
  updateAuthenticationSettings,
  updateAccountLockoutSettings
} from '../../http/user-management-services';
import DynamicFormFieldRenderer from '../Reusable/DynamicFormFieldRenderer';
import _, { clone, isEqual } from 'lodash';

interface SecurityActionProps {
  settingKey?: string;
  formData?: any;
  callback?: Function;
  setIsReadOnly?: Function;
  isReadOnly?: boolean;
  setIsLoading?: Function;
  resetSettings?: Function;
  initialData?: any;
  handleValidation?: Function;
  hasErrors?: Function;
  validateSave?: Function;
  settingType?: any;
}

const ActionButtons: React.FC<SecurityActionProps> = ({
  settingKey,
  formData,
  callback = () => {},
  isReadOnly = false,
  setIsReadOnly = () => {},
  setIsLoading = () => {},
  resetSettings = () => {},
  initialData,
  handleValidation = () => {},
  hasErrors = () => {},
  validateSave = () => {},
  settingType
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dispatchToastMessage = (toastMessage: any, error: boolean) => {
    dispatch(
      setToastData({
        toastMessage,
        isToastActive: true,
        toastType: error ? 'error' : 'success'
      })
    );
  };

  const updateSessionSettings = async (formData: any) => {
    setIsLoading(true);
    let clonedFormData = clone(formData);
    const { sessionQuotaExhaustedBehaviour } = clonedFormData;
    if (!sessionQuotaExhaustedBehaviour) {
      clonedFormData = {
        ...clonedFormData,
        sessionQuotaExhaustedBehaviour: 'DestroyNextExpiringAction'
      };
    }

    let toastMessage = `${t('T_SESSION_TOKEN_SETTINGS')} ${t('T_SETTINGS_UPDATED_SUCCESSFULLY')}`;
    const resp = await updateSessionTokenSettings(clonedFormData);
    if (resp.status === HTTP_STATUS.HTTP_OK) {
      dispatchToastMessage(toastMessage, false);
      callback(resp.data);
    } else {
      toastMessage = getFailureMessage(resp);
      dispatchToastMessage(toastMessage, true);
      setIsLoading(false);
    }
  };

  const updateOauthSettings = async (formData: any, clientType: any) => {
    setIsLoading(true);
    let toastMessage = `${t('T_OAUTH_TOKEN_SETTINGS')} ${t('T_SETTINGS_UPDATED_SUCCESSFULLY')}`;
    const resp = await updateOauthSettingsService(formData, clientType);
    if (resp.status === HTTP_STATUS.HTTP_OK) {
      dispatchToastMessage(toastMessage, false);
      callback(resp.data);
    } else {
      const toastMessage = getFailureMessage(resp);
      dispatchToastMessage(toastMessage, true);
      setIsLoading(false);
    }
  };

  const updateAuthSettings = async (formData: any) => {
    setIsLoading(true);
    const resp = await updateAuthenticationSettings(formData);
    let toastMessage = `${t('T_AUTHENTICATION_SETTINGS')} ${t('T_SETTINGS_UPDATED_SUCCESSFULLY')}`;
    if (resp.status === HTTP_STATUS.HTTP_OK) {
      dispatchToastMessage(toastMessage, false);
      callback(resp.data);
    } else {
      const toastMessage = getFailureMessage(resp);
      dispatchToastMessage(toastMessage, true);
      setIsLoading(false);
    }
  };

  const updateAccountLockOut = async (formData: any) => {
    setIsLoading(true);
    const resp = await updateAccountLockoutSettings(formData);
    let toastMessage = `${t('T_ACCOUNT_LOCKOUT_SETTINGS')} ${t('T_SETTINGS_UPDATED_SUCCESSFULLY')}`;
    if (resp.status === HTTP_STATUS.HTTP_OK) {
      dispatchToastMessage(toastMessage, false);
      callback(resp.data);
    } else {
      const toastMessage = getFailureMessage(resp);
      dispatchToastMessage(toastMessage, true);
      setIsLoading(false);
    }
  };

  // const updateSecuritySettings = (settingKey: any, formData: any, settingType: any) => {
  //   const errorObj = handleValidation();
  //   if (Object.values(errorObj).every((v) => v === false)) {
  //     switch (settingType) {
  //       case 'sessionToken':
  //         updateSessionSettings(formData);
  //         break;
  //       case 'oauthportal_client':
  //         updateOauthSettings(formData, 'portal_client');
  //         break;
  //       case 'oauthmobile_client':
  //         updateOauthSettings(formData, 'mobile_client');
  //         break;
  //       case 'oauthoidc_pkce':
  //         updateOauthSettings(formData, 'oidc_pkce');
  //         break;
  //       case 'oauthsvcac_client':
  //         updateOauthSettings(formData, 'svcac_client');
  //         break;
  //       case 'authSetting':
  //         updateAuthSettings(formData);
  //         break;
  //       case 'accountLockout':
  //         updateAccountLockOut(formData);
  //         break;
  //     }
  //   }
  // };

  const updateSecuritySettings = (settingKey: any, formData: any, settingType: any) => {
    const errorObj = handleValidation();
    if (Object.values(errorObj).every((v) => v === false)) {
      switch (settingKey) {
        case 'sessionToken':
          updateSessionSettings(formData);
          break;
        case 'oauthSettings':
          updateOauthSettings(formData, settingType);
          break;
        case 'authSetting':
          updateAuthSettings(formData);
          break;
        case 'accountLockout':
          updateAccountLockOut(formData);
          break;
      }
    }
  };

  const isPrimaryDisabled = (formData: any, initialData: any) => {
    return isEqual(formData, initialData);
  };

  return (
    <>
      {!isReadOnly && (
        <div className="mb-1 ml-auto mr-3 flex justify-end">
          <BiButton
            className={'mr-2 text-primary'}
            type="button"
            onClick={() => {
              resetSettings();
              setIsReadOnly(true);
            }}
          >
            {t('T_CANCEL')}
          </BiButton>
          <BiButton
            className={'float-right bg-primary text-white'}
            type="button"
            disabled={isPrimaryDisabled(formData, initialData) || validateSave()}
            onClick={() => {
              updateSecuritySettings(settingKey, formData, settingType);
            }}
          >
            {t('T_SAVE')}
          </BiButton>
        </div>
      )}
      {isReadOnly && (
        <div className="mb-1 ml-auto mr-3 flex justify-end">
          <BiButton
            className={'bg-primary text-white'}
            type="button"
            onClick={() => {
              setIsReadOnly(false);
            }}
          >
            {t('T_EDIT')}
          </BiButton>
        </div>
      )}
    </>
  );
};

export default ActionButtons;
